import { Mic } from "@safers/ui/atoms";
import { useCommandModalStore, selectOpenCommandModal } from "@/stores";

/**
 * 명령 팔레트 진입점.
 *
 * **데스크톱과 모바일의 아이콘이 다르다. 의도된 것이다.** 데스크톱은 키보드가 손 밑에
 * 있어 텍스트가 빠르지만, 폰에서 현장명을 타이핑하는 건 느리고 현장에서는 한 손·장갑
 * 상황이 흔하다. 돋보기로 들어가면 `모달 → 마이크 탭 → 말하기` 3단계인데 마이크로
 * 들어가면 1단계다. 아이콘이 곧 사용법을 설명한다는 점도 크다.
 */
export function CommandLauncher({
  variant,
  tone = "light",
}: {
  variant: "desktop" | "mobile";
  tone?: "light" | "dark";
}) {
  const open = useCommandModalStore(selectOpenCommandModal);
  const dark = tone === "dark";

  if (variant === "mobile") {
    return (
      <button
        type="button"
        onClick={() => open({ listen: true })}
        aria-label="Safers AI 에게 말하기"
        className={`flex size-9 shrink-0 items-center justify-center rounded-md transition-colors ${
          dark ? "text-white/70 hover:bg-white/10" : "text-neutral-600 hover:bg-neutral-200/60"
        }`}
      >
        <Mic size="sm" aria-hidden="true" />
      </button>
    );
  }

  /*
    맨 아이콘이 아니라 테두리 있는 필이다. 알람·계정 옆에서 아이콘 하나 더로 묻히면
    안 된다 — 이 기능은 이미 만들어져 있으면서도 "있는 줄 몰라서" 안 쓰이고 있었다.

    **안은 비우고 테두리만 브랜드색을 쓴다.** 옆의 `알림 보내기` 가 채운 브랜드 버튼이라,
    이쪽까지 색을 채우면 주황 덩어리로 읽힌다. 발송이 더 중요한 동작이므로 이쪽이
    시선을 뺏으면 안 된다.

    글자는 `--brand-primary-on-soft` 다 — 브랜드 원색(#ff7500)은 흰 바탕에서 대비가
    모자라고, 이 토큰이 그 목적으로 한 단계 어둡게 잡아 둔 값이다.
  */
  return (
    <button
      type="button"
      onClick={() => open()}
      aria-label="Safers AI 에게 묻기"
      title="Safers AI 에게 묻기"
      className={`flex h-8 shrink-0 items-center gap-2 rounded-full border border-brand py-0 pl-2.5 pr-2 text-[0.8125rem] transition-colors xl:w-56 xl:justify-between ${
        dark ? "bg-transparent hover:bg-brand/10" : "bg-white hover:bg-[var(--brand-primary-soft)]"
      }`}
    >
      <span className="flex min-w-0 items-center gap-2 text-brand">
        <SearchIcon />
        {/*
          라벨이 아니라 **말 걸기**다. 모달을 열면 같은 문구가 입력창 플레이스홀더로
          이어져, 이 자리가 그대로 펼쳐진 것처럼 읽힌다. 검색창의 플레이스홀더처럼
          흐린 색을 써야 "적혀 있는 값" 이 아니라 "여기에 쓰세요" 로 보인다.
        */}
        <span
          className={`hidden truncate font-normal xl:inline ${
            dark ? "text-white/40" : "text-neutral-400"
          }`}
        >
          Safers AI 에게 물어보세요
        </span>
      </span>
      {/* 좁아지면 문구부터 접고, 더 좁으면 뱃지도 접어 아이콘만 남긴다. */}
      <kbd
        className={`hidden shrink-0 rounded border border-brand/30 px-1.5 py-0.5 text-[0.6875rem] font-medium lg:inline ${
          dark ? "bg-brand/10 text-brand" : "bg-transparent text-[var(--brand-primary-on-soft)]"
        }`}
      >
        {shortcutLabel()}
      </kbd>
    </button>
  );
}

/** Mac 에 `Ctrl K` 라고 써 두면 눌러도 안 되는 안내가 된다. */
function shortcutLabel(): string {
  const mac = typeof navigator !== "undefined" && /Mac|iPhone|iPad/.test(navigator.platform);
  return mac ? "⌘K" : "Ctrl K";
}

function SearchIcon() {
  return (
    <svg
      className="size-[1.125rem]"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      aria-hidden="true"
    >
      <circle cx="11" cy="11" r="7" />
      <path d="M20 20l-3.5-3.5" />
    </svg>
  );
}
